/**
 * PostHog adapter implementation
 */
import { AnalyticsAdapter, AnalyticsEvent, AnalyticsUser } from '../types';

export class PostHogAdapter implements AnalyticsAdapter {
  private isBlockedByClient = false;
  private posthogInstance: any = null;

  initialize(options: { apiKey: string; apiHost?: string }) {
    try {
      // Dynamically import posthog to handle blocking gracefully
      import('posthog-js')
        .then(module => {
          const posthog = module.default;
          this.posthogInstance = posthog;

          posthog.init(options.apiKey, {
            api_host: options.apiHost || 'https://app.posthog.com',
            capture_pageview: false, // We'll handle this manually
            autocapture: false,      // We'll define our events explicitly
            capture_pageleave: true, // Enable page leave events tracking
            loaded: (loadedPostHog) => {
              // Disable analytics in development mode unless explicitly enabled
              if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_ANALYTICS) {
                loadedPostHog.opt_out_capturing();
              }
            }
          });
        })
        .catch(error => {
          // Handle the case where the import is blocked
          console.debug('Analytics blocked or failed to load:', error);
          this.isBlockedByClient = true;
        });
    } catch (error) {
      // Fallback error handling
      console.debug('Analytics initialization failed:', error);
      this.isBlockedByClient = true;
    }
  }

  identify(user: AnalyticsUser) {
    if (this.isBlockedByClient || !this.posthogInstance) return;
    try {
      this.posthogInstance.identify(user.id, user.traits);
    } catch (error) {
      console.debug('Analytics identify failed:', error);
    }
  }

  track(event: AnalyticsEvent) {
    if (this.isBlockedByClient || !this.posthogInstance) return;
    try {
      this.posthogInstance.capture(event.name, event.properties);
    } catch (error) {
      console.debug('Analytics track failed:', error);
    }
  }

  page(name: string, properties?: Record<string, any>) {
    if (this.isBlockedByClient || !this.posthogInstance) return;
    try {
      this.posthogInstance.capture('$pageview', {
        ...properties,
        page_name: name,
      });
    } catch (error) {
      console.debug('Analytics page view failed:', error);
    }
  }

  reset() {
    if (this.isBlockedByClient || !this.posthogInstance) return;
    try {
      this.posthogInstance.reset();
    } catch (error) {
      console.debug('Analytics reset failed:', error);
    }
  }

  enable() {
    if (this.isBlockedByClient || !this.posthogInstance) return;
    try {
      this.posthogInstance.opt_in_capturing();
    } catch (error) {
      console.debug('Analytics enable failed:', error);
    }
  }

  disable() {
    if (this.isBlockedByClient || !this.posthogInstance) return;
    try {
      this.posthogInstance.opt_out_capturing();
    } catch (error) {
      console.debug('Analytics disable failed:', error);
    }
  }
}